import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Plus, Search, X, Menu as MenuIcon, MapPin, Phone, Mail, User, ChevronDown, Check, CheckCircle2, QrCode, ShoppingCart, Bike, CalendarClock, Calendar, Trash2, Settings as SettingsIcon, Sparkles, Power, ChefHat, Percent, Landmark } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { Toggle } from './Toggle';
import { useBranches, Branch, BranchOrderMethods, methodMeta } from './BranchesContext';
import { BranchHoursSection } from './BranchHoursSection';
import { BranchStaffSection } from './BranchStaffSection';
import { BranchStationsSection } from './BranchStationsSection';
import { BranchOutletsSection } from './BranchOutletsSection';
import { BranchOrderingSection } from './BranchOrderingSection';
import { PaymentMethod, paymentMethodLabel } from './OrdersContext';
import { useMenus, catalogItems } from './MenusContext';
const methodIcons: Record<keyof BranchOrderMethods, ComponentType<{
  size?: number;
}>> = {
  dineIn: QrCode,
  takeout: ShoppingCart,
  delivery: Bike,
  orderAhead: CalendarClock
};
const colorMap: Record<string, {
  ring: string;
  bg: string;
  text: string;
  chip: string;
  iconBg: string;
}> = {
  purple: {
    ring: 'ring-purple-200',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    chip: 'bg-purple-100 text-purple-700',
    iconBg: 'bg-purple-100 text-purple-600'
  },
  orange: {
    ring: 'ring-orange-200',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    chip: 'bg-orange-100 text-orange-700',
    iconBg: 'bg-orange-100 text-orange-600'
  },
  blue: {
    ring: 'ring-blue-200',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    chip: 'bg-blue-100 text-blue-700',
    iconBg: 'bg-blue-100 text-blue-600'
  },
  emerald: {
    ring: 'ring-emerald-200',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    chip: 'bg-emerald-100 text-emerald-700',
    iconBg: 'bg-emerald-100 text-emerald-600'
  }
};
export function BranchesPage() {
  const {
    branches,
    menus,
    updateBranch,
    addBranch,
    deleteBranch
  } = useBranches();
  const {
    branchOverrides,
    setBranchItemAvailability,
    setBranchItemPrice
  } = useMenus();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(branches[0]?.id || null);
  const [search, setSearch] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const filtered = useMemo(() => {
    if (!search.trim()) return branches;
    const q = search.toLowerCase();
    return branches.filter(b => b.name.toLowerCase().includes(q) || b.address.toLowerCase().includes(q) || (b.manager || '').toLowerCase().includes(q));
  }, [branches, search]);
  const selected = branches.find(b => b.id === selectedId) || filtered[0] || null;
  const flashSaved = () => {
    setSavedFlash(true);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1200);
  };
  const update = (patch: Partial<Branch>) => {
    if (!selected) return;
    updateBranch(selected.id, patch);
    flashSaved();
  };
  const handleAdd = () => {
    const id = addBranch();
    setSelectedId(id);
  };
  const handleDelete = (id: string) => {
    if (!window.confirm('Delete this branch? This cannot be undone.')) return;
    deleteBranch(id);
    setSelectedId(branches.find(b => b.id !== id)?.id || null);
  };
  const activeCount = branches.filter(b => b.active).length;
  return <div className="flex h-screen w-full bg-[#f9fafb] overflow-hidden font-sans flex-col md:flex-row" data-id="element-377">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between bg-[#0f172a] text-white p-4 flex-shrink-0 z-30" data-id="element-378">
        <button onClick={() => setIsSidebarOpen(true)} className="p-1 hover:bg-white/10 rounded-md transition-colors" data-id="element-379">
          <MenuIcon size={24} data-id="element-380" />
        </button>
        <span className="font-semibold" data-id="element-381">Branches</span>
        <div className="w-6" data-id="element-382" />
      </div>

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(v => !v)} data-id="element-383" />

      <main className="flex-1 flex flex-col h-full overflow-hidden" data-id="element-384">
        {/* Header */}
        <div className="px-4 md:px-6 pt-5 md:pt-6 pb-4 border-b border-gray-200 bg-white flex-shrink-0" data-id="element-385">
          <div className="flex items-end justify-between gap-3 mb-4 flex-wrap" data-id="element-386">
            <div data-id="element-387">
              <div className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1 flex items-center gap-1.5" data-id="element-388">
                <Building2 size={13} data-id="element-389" />
                Operations
              </div>
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-390">Branches</h1>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-391">
                {branches.length} branches · {activeCount} active
              </p>
            </div>
            <button onClick={handleAdd} className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors shadow-sm" data-id="element-392">
              <Plus size={16} data-id="element-393" />
              Add branch
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[360px_1fr]" data-id="element-394">
          {/* List */}
          <div className="overflow-y-auto border-r border-gray-200 bg-gray-50/40 p-4 space-y-2" data-id="element-395">
            <div className="relative mb-2" data-id="element-396">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-397" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search branches..." className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-398" />
            </div>
            {filtered.map(b => <BranchCard key={b.id} branch={b} isSelected={selected?.id === b.id} onClick={() => setSelectedId(b.id)} data-id="element-399" />)}
            {filtered.length === 0 && <div className="p-8 text-center text-sm text-gray-500" data-id="element-400">
                No branches match your search.
              </div>}
          </div>

          {/* Detail */}
          <div className="overflow-y-auto bg-white" data-id="element-401">
            {selected ? (() => {
            // Alias the selected branch + its update/delete handlers into the
            // local scope so the JSX block below (originally lifted from a
            // BranchDetail helper) can keep using `branch`, `onUpdate`, and
            // `onDelete` without redeclaration.
            const branch = selected;
            const onUpdate = update;
            const onDelete = () => handleDelete(selected.id);
            return <div className="max-w-3xl mx-auto p-6 lg:p-8 space-y-8 pb-24" data-id="element-402">
                    <div className="flex items-center justify-between border-b pb-4" data-id="element-403">
                      <div className="flex-1 min-w-0" data-id="element-404">
                        <input value={branch.name} onChange={e => onUpdate({
                    name: e.target.value
                  })} className="text-2xl font-bold text-gray-900 bg-transparent outline-none w-full -ml-1 px-1 rounded-md focus:bg-orange-50 focus:ring-2 focus:ring-orange-100 transition" data-id="element-405" />
                        <div className="text-xs text-gray-500 mt-1" data-id="element-406">
                          Created{' '}
                          {new Date(branch.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-407">
                        <AnimatePresence data-id="element-408">
                          {savedFlash && <motion.span initial={{
                      opacity: 0,
                      y: -4
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0
                    }} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold bg-emerald-100 text-emerald-700" data-id="element-409">
                              <Check size={11} strokeWidth={3} data-id="element-410" />
                              Saved
                            </motion.span>}
                        </AnimatePresence>
                        <button onClick={onDelete} className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete branch" aria-label="Delete branch" data-id="element-411">
                          <Trash2 size={16} data-id="element-412" />
                        </button>
                      </div>
                    </div>

                    {/* Live toggle (prominent) */}
                    <div className={`flex items-center justify-between gap-4 rounded-xl border p-4 mb-6 ${branch.active ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`} data-id="element-413">
                      <div className="flex items-center gap-3" data-id="element-414">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${branch.active ? 'bg-emerald-500 text-white' : 'bg-gray-300 text-gray-600'}`} data-id="element-415">
                          <Power size={18} data-id="element-416" />
                        </div>
                        <div data-id="element-417">
                          <div className="font-bold text-gray-900 text-sm" data-id="element-418">
                            {branch.active ? 'Taking orders' : 'Paused'}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-419">
                            {branch.active ? 'Customers can place orders at this branch' : 'New orders are paused for this branch'}
                          </div>
                        </div>
                      </div>
                      <Toggle checked={branch.active} onChange={v => onUpdate({
                  active: v
                })} data-id="element-420" />
                    </div>

                    {/* Basic info */}
                    <FormSection title="Basic information" data-id="element-421">
                      <Field label="Address" icon={<MapPin size={14} data-id="element-423" />} data-id="element-422">
                        <textarea value={branch.address} onChange={e => onUpdate({
                    address: e.target.value
                  })} placeholder="Street address, district, city" rows={2} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition resize-none" data-id="element-424" />
                      </Field>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-425">
                        <Field label="Phone number" icon={<Phone size={14} data-id="element-427" />} data-id="element-426">
                          <input type="tel" value={branch.phone} onChange={e => onUpdate({
                      phone: e.target.value
                    })} placeholder="+251 911 000 000" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-428" />
                        </Field>
                        <Field label="Email" icon={<Mail size={14} data-id="element-430" />} data-id="element-429">
                          <input type="email" value={branch.email} onChange={e => onUpdate({
                      email: e.target.value
                    })} placeholder="branch@example.com" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-431" />
                        </Field>
                      </div>
                      <Field label="Branch manager" icon={<User size={14} data-id="element-433" />} data-id="element-432">
                        <input value={branch.manager || ''} onChange={e => onUpdate({
                    manager: e.target.value
                  })} placeholder="Name of the manager" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-434" />
                      </Field>
                    </FormSection>

                    {/* Menu */}
                    <FormSection title="Default menu" description="Which menu is shown to customers at this branch" data-id="element-435">
                      <Field label="Menu" icon={<ChefHat size={14} data-id="element-437" />} data-id="element-436">
                        <div className="relative" data-id="element-438">
                          <select value={branch.defaultMenuId} onChange={e => onUpdate({
                      defaultMenuId: e.target.value
                    })} className="w-full appearance-none pl-3 pr-10 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition" data-id="element-439">
                            {menus.map(m => <option key={m.id} value={m.id} data-id="element-440">
                                {m.name}
                              </option>)}
                          </select>
                          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-441" />
                        </div>
                      </Field>
                    </FormSection>

                    {/* Ordering & QR Codes */}
                    <FormSection title="Ordering & QR Codes" description="Configure how customers can order and generate the QR codes they'll scan." data-id="element-442">
                      <BranchOrderingSection branch={branch} onUpdate={onUpdate} data-id="element-443" />
                    </FormSection>

                    {/* Taxes & Fees */}
                    <FormSection title="Taxes & Fees" description="Tax rate and service fee applied to orders at this branch" data-id="element-444">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-445">
                        <Field label="Tax Rate (%)" icon={<Percent size={14} data-id="element-447" />} data-id="element-446">
                          <input type="number" value={branch.taxRatePct} onChange={e => onUpdate({
                      taxRatePct: Number(e.target.value) || 0
                    })} min="0" max="100" step="0.1" className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-448" />
                        </Field>
                        <Field label="Service Fee (%)" icon={<Percent size={14} data-id="element-450" />} data-id="element-449">
                          <input type="number" value={branch.serviceFeePct} onChange={e => onUpdate({
                      serviceFeePct: Number(e.target.value) || 0
                    })} min="0" max="100" step="0.1" className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-451" />
                        </Field>
                      </div>
                    </FormSection>

                    {/* Payment Methods */}
                    <FormSection title="Payment Methods" description="Which payment methods customers can use at this branch" data-id="element-452">
                      <div className="grid grid-cols-2 gap-4" data-id="element-453">
                        {(Object.keys(branch.acceptedPaymentMethods) as PaymentMethod[]).map(key => <div key={key} className="flex items-center justify-between p-3 rounded-xl border border-gray-200 bg-white" data-id="element-454">
                            <span className="text-sm font-semibold text-gray-700" data-id="element-455">
                              {paymentMethodLabel[key]}
                            </span>
                            <Toggle checked={branch.acceptedPaymentMethods[key]} onChange={v => onUpdate({
                      acceptedPaymentMethods: {
                        ...branch.acceptedPaymentMethods,
                        [key]: v
                      }
                    })} data-id="element-456" />
                          </div>)}
                      </div>
                    </FormSection>

                    {/* Bank Accounts */}
                    {branch.acceptedPaymentMethods['bank-transfer'] && <FormSection title="Bank Accounts" description="Manage bank accounts for direct transfers" data-id="element-457">
                        <div className="space-y-3" data-id="element-458">
                          {branch.bankAccounts?.map((account, idx) => <div key={account.id} className="flex items-start justify-between p-4 rounded-xl border border-gray-200 bg-white gap-4" data-id="element-459">
                              <div className="flex-1 space-y-3" data-id="element-460">
                                <div className="grid grid-cols-2 gap-3" data-id="element-461">
                                  <div data-id="element-462">
                                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-463">
                                      Bank Name
                                    </label>
                                    <input value={account.bankName} onChange={e => {
                            const newAccounts = [...(branch.bankAccounts || [])];
                            newAccounts[idx] = {
                              ...account,
                              bankName: e.target.value
                            };
                            onUpdate({
                              bankAccounts: newAccounts
                            });
                          }} placeholder="e.g. CBE" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400" data-id="element-464" />
                                  </div>
                                  <div data-id="element-465">
                                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-466">
                                      Label (Optional)
                                    </label>
                                    <input value={account.label || ''} onChange={e => {
                            const newAccounts = [...(branch.bankAccounts || [])];
                            newAccounts[idx] = {
                              ...account,
                              label: e.target.value
                            };
                            onUpdate({
                              bankAccounts: newAccounts
                            });
                          }} placeholder="e.g. Main Account" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400" data-id="element-467" />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3" data-id="element-468">
                                  <div data-id="element-469">
                                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-470">
                                      Account Name
                                    </label>
                                    <input value={account.accountName} onChange={e => {
                            const newAccounts = [...(branch.bankAccounts || [])];
                            newAccounts[idx] = {
                              ...account,
                              accountName: e.target.value
                            };
                            onUpdate({
                              bankAccounts: newAccounts
                            });
                          }} placeholder="e.g. Demo Restaurant PLC" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400" data-id="element-471" />
                                  </div>
                                  <div data-id="element-472">
                                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-473">
                                      Account Number
                                    </label>
                                    <input value={account.accountNumber} onChange={e => {
                            const newAccounts = [...(branch.bankAccounts || [])];
                            newAccounts[idx] = {
                              ...account,
                              accountNumber: e.target.value
                            };
                            onUpdate({
                              bankAccounts: newAccounts
                            });
                          }} placeholder="e.g. 1000123456789" className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400" data-id="element-474" />
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 pt-1" data-id="element-475">
                                  <Toggle checked={account.active} onChange={v => {
                          const newAccounts = [...(branch.bankAccounts || [])];
                          newAccounts[idx] = {
                            ...account,
                            active: v
                          };
                          onUpdate({
                            bankAccounts: newAccounts
                          });
                        }} data-id="element-476" />
                                  <span className="text-xs text-gray-600 font-medium" data-id="element-477">
                                    Active
                                  </span>
                                </div>
                              </div>
                              <button onClick={() => {
                      const newAccounts = branch.bankAccounts?.filter((_, i) => i !== idx);
                      onUpdate({
                        bankAccounts: newAccounts
                      });
                    }} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-id="element-478">
                                <Trash2 size={16} data-id="element-479" />
                              </button>
                            </div>)}
                          <button onClick={() => {
                    const newAccount = {
                      id: `ba_${Date.now().toString(36)}`,
                      bankName: '',
                      accountName: '',
                      accountNumber: '',
                      active: true
                    };
                    onUpdate({
                      bankAccounts: [...(branch.bankAccounts || []), newAccount]
                    });
                  }} className="w-full py-3 rounded-xl border-2 border-dashed border-gray-200 text-sm font-bold text-gray-500 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50 transition-colors flex items-center justify-center gap-2" data-id="element-480">
                            <Plus size={16} data-id="element-481" />
                            Add Bank Account
                          </button>
                        </div>
                      </FormSection>}

                    {/* Customer tipping */}
                    <FormSection title="Customer tipping" description="Show customers tip presets at checkout for this branch" data-id="element-482">
                      <div className="space-y-4" data-id="element-483">
                        <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-200" data-id="element-484">
                          <div className="min-w-0" data-id="element-485">
                            <div className="text-sm font-semibold text-gray-900" data-id="element-486">
                              Enable customer tipping
                            </div>
                            <div className="text-[11px] text-gray-500" data-id="element-487">
                              When on, customers see tip presets at checkout for
                              this branch.
                            </div>
                          </div>
                          <Toggle checked={!!branch.tippingOverride} onChange={v => onUpdate({
                      tippingOverride: v ? {
                        customerTippingEnabled: true,
                        suggestedTipPcts: [10, 15, 20]
                      } : undefined
                    })} data-id="element-488" />
                        </div>

                        {branch.tippingOverride && <div data-id="element-489">
                            <div className="grid grid-cols-3 gap-3" data-id="element-490">
                              {[0, 1, 2].map(idx => <Field key={idx} label={`Preset ${idx + 1} (%)`} data-id="element-491">
                                  <input type="number" value={branch.tippingOverride!.suggestedTipPcts[idx] ?? 0} onChange={e => {
                          const next = [...branch.tippingOverride!.suggestedTipPcts];
                          next[idx] = Number(e.target.value) || 0;
                          onUpdate({
                            tippingOverride: {
                              ...branch.tippingOverride!,
                              suggestedTipPcts: next
                            }
                          });
                        }} min="0" max="100" step="1" className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-492" />
                                </Field>)}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-2" data-id="element-493">
                              Shown to customers as quick-pick chips at
                              checkout. They can also enter a custom amount.
                            </p>
                          </div>}
                      </div>
                    </FormSection>

                    {/* Menu & Pricing */}
                    <FormSection title="Menu & Pricing" description="Manage which items are available at this branch and set branch-specific prices." data-id="element-494">
                      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-495">
                        <div className="grid grid-cols-[1fr_auto_120px] gap-4 p-4 border-b border-gray-100 bg-gray-50 text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-496">
                          <div data-id="element-497">Item</div>
                          <div className="text-center" data-id="element-498">Available</div>
                          <div className="text-right" data-id="element-499">Branch Price</div>
                        </div>
                        <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto" data-id="element-500">
                          {catalogItems.map(item => {
                      const override = branchOverrides[branch.id]?.[item.id] || {};
                      const isAvailable = override.available ?? true;
                      const price = override.price ?? item.price;
                      return <div key={item.id} className={`grid grid-cols-[1fr_auto_120px] gap-4 p-4 items-center transition-colors ${!isAvailable ? 'bg-gray-50 opacity-60' : ''}`} data-id="element-501">
                                <div data-id="element-502">
                                  <div className="font-bold text-sm text-gray-900" data-id="element-503">
                                    {item.name}
                                  </div>
                                  <div className="text-xs text-gray-500" data-id="element-504">
                                    {item.category}
                                  </div>
                                </div>
                                <div className="flex justify-center" data-id="element-505">
                                  <Toggle checked={isAvailable} onChange={v => setBranchItemAvailability(branch.id, item.id, v)} data-id="element-506" />
                                </div>
                                <div data-id="element-507">
                                  <div className="relative" data-id="element-508">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium" data-id="element-509">
                                      ETB
                                    </span>
                                    <input type="number" value={override.price !== undefined ? override.price : ''} placeholder={item.price.toString()} onChange={e => {
                              const val = e.target.value;
                              setBranchItemPrice(branch.id, item.id, val ? Number(val) : undefined);
                            }} disabled={!isAvailable} className="w-full pl-10 pr-3 py-2 rounded-lg border border-gray-200 text-sm font-bold tabular-nums focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 disabled:bg-gray-100" data-id="element-510" />
                                  </div>
                                </div>
                              </div>;
                    })}
                        </div>
                      </div>
                    </FormSection>

                    {/* Outlets & Concepts */}
                    <FormSection title="Outlets & Concepts" description="Manage distinct dining concepts or brands within this location, each with its own customer menu and QR code." data-id="element-511">
                      <BranchOutletsSection branch={branch} data-id="element-512" />
                    </FormSection>

                    {/* Operating Hours */}
                    <FormSection title="Operating Hours" description="Operating hours, per-method overrides, holidays and temporary closures" data-id="element-513">
                      <BranchHoursSection branch={branch} onUpdate={onUpdate} data-id="element-514" />
                    </FormSection>

                    {/* Kitchen stations */}
                    <FormSection title="Kitchen stations" description="Prep areas at this branch. Items route to one station; the kitchen display can be viewed per-station or as an expediter overview." data-id="element-515">
                      <BranchStationsSection branch={branch} data-id="element-516" />
                    </FormSection>

                    {/* Staff & permissions */}
                    <FormSection title="Staff & permissions" description="Assign managers and staff with branch-level access and SMS routing" data-id="element-517">
                      <BranchStaffSection branch={branch} data-id="element-518" />
                    </FormSection>

                    {/* Reservations */}
                    <FormSection title="Reservations" description="Allow customers to book tables in advance" data-id="element-519">
                      <ReservationsRow enabled={branch.reservationsEnabled} onChange={v => onUpdate({
                  reservationsEnabled: v
                })} data-id="element-520" />
                    </FormSection>
                  </div>;
          })() : <div className="h-full flex items-center justify-center p-10 text-center" data-id="element-521">
                <div data-id="element-522">
                  <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-523">
                    <Building2 size={22} className="text-gray-400" data-id="element-524" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm mb-1" data-id="element-525">
                    No branches yet
                  </h3>
                  <p className="text-xs text-gray-500 mb-3" data-id="element-526">
                    Add a branch to get started.
                  </p>
                  <button onClick={handleAdd} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 text-white hover:bg-orange-600 transition-colors" data-id="element-527">
                    <Plus size={14} data-id="element-528" />
                    Add branch
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </main>
    </div>;
}
function BranchCard({
  branch,
  isSelected,
  onClick
}: {
  branch: Branch;
  isSelected: boolean;
  onClick: () => void;
}) {
  const enabledMethods = Object.values(branch.methods).filter(Boolean).length;
  return <motion.button layout onClick={onClick} className={`w-full text-left p-4 rounded-xl border transition-colors relative ${isSelected ? 'bg-white border-orange-300 ring-2 ring-orange-100 shadow-sm' : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'}`} data-id="element-529">
      <div className="flex items-start justify-between gap-2 mb-2" data-id="element-530">
        <div className="flex-1 min-w-0" data-id="element-531">
          <div className="flex items-center gap-2" data-id="element-532">
            <h3 className="font-bold text-gray-900 text-[14px] truncate" data-id="element-533">
              {branch.name}
            </h3>
          </div>
          {branch.address && <div className="flex items-start gap-1 text-[11px] text-gray-500 mt-1" data-id="element-534">
              <MapPin size={11} className="flex-shrink-0 mt-0.5" data-id="element-535" />
              <span className="truncate" data-id="element-536">{branch.address}</span>
            </div>}
        </div>
        <BranchStatusPill branch={branch} data-id="element-537" />
      </div>
      <div className="flex items-center justify-between gap-2 text-[11px] text-gray-500" data-id="element-538">
        <div className="flex items-center gap-1" data-id="element-539">
          <span className="font-semibold text-gray-700" data-id="element-540">{enabledMethods}</span>
          method{enabledMethods === 1 ? '' : 's'} on
          {branch.reservationsEnabled && <>
              <span className="text-gray-300" data-id="element-541">·</span>
              <Calendar size={11} className="text-blue-500" data-id="element-542" />
              <span className="text-blue-700 font-semibold" data-id="element-543">Reservations</span>
            </>}
        </div>
        {branch.manager && <span className="truncate text-gray-400" data-id="element-544">{branch.manager}</span>}
      </div>
    </motion.button>;
}
function BranchStatusPill({
  branch
}: {
  branch: Branch;
}) {
  if (branch.temporaryClosure?.active) {
    return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-700" data-id="element-545">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" data-id="element-546" />
        Closed
      </span>;
  }
  return <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide ${branch.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-547">
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${branch.active ? 'bg-emerald-500' : 'bg-gray-400'}`} data-id="element-548" />
      {branch.active ? 'Live' : 'Paused'}
    </span>;
}
function FormSection({
  title,
  description,
  children
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return <section className="mb-7" data-id="element-549">
      <div className="mb-3" data-id="element-550">
        <h3 className="font-bold text-gray-900 text-sm" data-id="element-551">{title}</h3>
        {description && <p className="text-xs text-gray-500 mt-0.5" data-id="element-552">{description}</p>}
      </div>
      <div className="space-y-4" data-id="element-553">{children}</div>
    </section>;
}
function Field({
  label,
  icon,
  children
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return <label className="block" data-id="element-554">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-555">
        {icon}
        {label}
      </div>
      {children}
    </label>;
}
function ReservationsRow({
  enabled,
  onChange
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return <div className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${enabled ? 'border-transparent bg-blue-50 ring-1 ring-blue-200' : 'border-gray-200 bg-white'}`} data-id="element-556">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`} data-id="element-557">
        <Calendar size={16} data-id="element-558" />
      </div>
      <div className="flex-1 min-w-0" data-id="element-559">
        <div className="flex items-center justify-between gap-2" data-id="element-560">
          <span className={`font-bold text-sm ${enabled ? 'text-gray-900' : 'text-gray-600'}`} data-id="element-561">
            Accept reservations
          </span>
          <Toggle checked={enabled} onChange={onChange} data-id="element-562" />
        </div>
        <p className="text-[11px] text-gray-500 mt-0.5 leading-snug" data-id="element-563">
          Customers can book tables ahead through the customer menu
        </p>
      </div>
    </div>;
}